// Theme Customization API Service

import type {
  ThemeConfig,
  ThemeVersion,
  ThemeVersionDetail,
  CreateThemeVersionRequest,
  UpdateThemeVersionRequest,
  UpdateThemeConfigRequest,
  ThemeConfigResponse,
  ThemeVersionsResponse,
  ThemeVersionDetailResponse,
  DeviceType,
  DeviceTypeParam,
} from '../types/theme';

const API_BASE = `${import.meta.env.VITE_API_BASE_URL || ''}/api/themes`;

/**
 * Get active theme configuration for a device type
 */
export async function getActiveTheme(device: DeviceType = 'desktop'): Promise<ThemeConfig> {
  const url = `${API_BASE}/active?device=${device}`;
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch active theme: ${response.statusText}`);
  }

  const data: ThemeConfigResponse = await response.json();
  return data.data;
}

/**
 * Get active theme as CSS variables
 * Returns CSS string for direct injection
 */
export async function getActiveThemeCSS(device: DeviceType = 'desktop'): Promise<string> {
  const url = `${API_BASE}/active/css?device=${device}`;
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch theme CSS: ${response.statusText}`);
  }

  return await response.text();
}

/**
 * List all theme versions (admin only)
 */
export async function listThemeVersions(): Promise<ThemeVersion[]> {
  const token = localStorage.getItem('auth_token');
  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(API_BASE, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to list theme versions: ${response.statusText}`);
  }

  const data: ThemeVersionsResponse = await response.json();
  return data.data;
}

/**
 * Get detailed theme version (admin only)
 */
export async function getThemeVersion(version: string): Promise<ThemeVersionDetail> {
  const token = localStorage.getItem('auth_token');
  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(`${API_BASE}/${version}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to get theme version: ${response.statusText}`);
  }

  const data: ThemeVersionDetailResponse = await response.json();
  return data.data;
}

/**
 * Create new theme version (admin only)
 */
export async function createThemeVersion(
  request: CreateThemeVersionRequest
): Promise<ThemeVersionDetail> {
  const token = localStorage.getItem('auth_token');
  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(API_BASE, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || 'Failed to create theme version');
  }

  const data: ThemeVersionDetailResponse = await response.json();
  return data.data;
}

/**
 * Update theme version metadata (admin only)
 */
export async function updateThemeVersion(
  version: string,
  request: UpdateThemeVersionRequest
): Promise<ThemeVersionDetail> {
  const token = localStorage.getItem('auth_token');
  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(`${API_BASE}/${version}`, {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || 'Failed to update theme version');
  }

  const data: ThemeVersionDetailResponse = await response.json();
  return data.data;
}

/**
 * Update theme configuration for a device (admin only)
 */
export async function updateThemeConfig(
  version: string,
  device: DeviceTypeParam,
  request: UpdateThemeConfigRequest
): Promise<ThemeConfig> {
  const token = localStorage.getItem('auth_token');
  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(`${API_BASE}/${version}/config/${device}`, {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || 'Failed to update theme config');
  }

  const data: ThemeConfigResponse = await response.json();
  return data.data;
}

/**
 * Delete theme version (admin only)
 */
export async function deleteThemeVersion(version: string): Promise<{ deleted: string }> {
  const token = localStorage.getItem('auth_token');
  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(`${API_BASE}/${version}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || 'Failed to delete theme version');
  }

  return await response.json();
}

/**
 * Inject theme CSS variables into document
 */
export function injectThemeCSS(css: string): void {
  // Remove existing theme style tag if present
  const existing = document.getElementById('theme-variables');
  if (existing) {
    existing.remove();
  }

  // Create new style tag
  const style = document.createElement('style');
  style.id = 'theme-variables';
  style.textContent = css;

  // Insert into head
  document.head.appendChild(style);
}

/**
 * Load and apply active theme for current device
 */
export async function loadActiveTheme(): Promise<void> {
  const device = window.innerWidth >= 768 ? 'desktop' : 'mobile';
  try {
    const css = await getActiveThemeCSS(device);
    injectThemeCSS(css);
  } catch (error) {
    console.error('Failed to load active theme:', error);
    // Fallback to default CSS if needed
  }
}

/**
 * Import theme version from JSON (admin only)
 */
export async function importTheme(data: {
  versionId?: string;
  desktop: ThemeConfig;
  mobile: ThemeConfig;
  name?: string;
  description?: string;
}): Promise<ThemeVersionDetail> {
  const token = localStorage.getItem('auth_token');
  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(`${API_BASE}/import`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || 'Failed to import theme');
  }

  const result: ThemeVersionDetailResponse = await response.json();
  return result.data;
}
